import React, { useState, useEffect, useCallback, useRef } from 'react';
import useOpenFromQuery from '../../../hooks/useOpenFromQuery';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import SupplierReturnNoteForm from './SupplierReturnNoteForm';
import SupplierReturnNoteDetails from './SupplierReturnNoteDetails';

const SupplierReturnNoteManagement = () => {
  const navigate = useNavigate();
  const handleBack = useDetailBack('/inventory/supplier-return-notes');
  const { hasPermission } = useAuth();

  // Permission constants for supplier return notes
  const PERMISSIONS = {
    READ: ['INVENTORY:SUPPLIER_RETURN_NOTES:READ', 'INVENTORY:ADMIN'],
    CREATE: ['INVENTORY:SUPPLIER_RETURN_NOTES:CREATE', 'INVENTORY:ADMIN'],
    UPDATE: ['INVENTORY:SUPPLIER_RETURN_NOTES:UPDATE', 'INVENTORY:ADMIN'],
    DELETE: ['INVENTORY:SUPPLIER_RETURN_NOTES:DELETE', 'INVENTORY:ADMIN'],
    APPROVE: ['INVENTORY:SUPPLIER_RETURN_NOTES:APPROVE', 'INVENTORY:ADMIN'],
    PROCESS: ['INVENTORY:SUPPLIER_RETURN_NOTES:PROCESS', 'INVENTORY:ADMIN']
  };

  // Check if user has read permission to access this component
  if (!hasPermission(PERMISSIONS.READ)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-xl text-gray-600 mt-4">
            You don't have permission to view supplier return notes.
          </p>
          <button
            onClick={handleBack}
            className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const [returnNotes, setReturnNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('supplierReturnNoteManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [sortField, setSortField] = useState('returnDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingReturnNote, setEditingReturnNote] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedReturnNote, setSelectedReturnNote] = useState(null);

  // Debounce timer ref
  const searchDebounceRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading

  const returnNoteStatuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'processed', label: 'Processed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' }
  ];

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, supplierFilter, sortField, sortOrder]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, supplierFilter, sortField, sortOrder]);

  // Add separate useEffect to log state changes
  useEffect(() => {
  }, [currentPage, totalItems, totalPages, returnNotes.length]);  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query parameters for backend filtering and pagination
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort: sortField,
        order: sortOrder,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(supplierFilter !== 'all' && { supplier: supplierFilter })
      };


      const [returnNotesResponse, suppliersResponse] = await Promise.all([
        inventoryApi.getSupplierReturnNotes(params),
        inventoryApi.getSuppliers()
      ]);


      // The API service already returns response.data, so returnNotesResponse should be the actual data
      const returnNotesResult = returnNotesResponse;


      if (returnNotesResult && returnNotesResult.success) {
        // Backend returns success wrapper with data and pagination
        setReturnNotes(returnNotesResult.data || []);
        const totalItemsCount = returnNotesResult.pagination?.total || 0;
        const totalPagesCount = returnNotesResult.pagination?.pages || Math.max(1, Math.ceil(totalItemsCount / itemsPerPage));
        setTotalItems(totalItemsCount);
        setTotalPages(totalPagesCount);

        // Ensure current page doesn't exceed total pages
        if (currentPage > totalPagesCount && totalPagesCount > 0) {
          console.warn('⚠️ Current page exceeds total pages, resetting to last page');
          setCurrentPage(totalPagesCount);
        }

      } else if (Array.isArray(returnNotesResult)) {
        // Fallback: direct array response
        const items = returnNotesResult;
        setReturnNotes(items);
        setTotalItems(items.length);
        const calculatedPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
        setTotalPages(calculatedPages);

        // Ensure current page doesn't exceed total pages
        if (currentPage > calculatedPages) {
          setCurrentPage(calculatedPages);
        }
      } else {
        console.error('❌ Unexpected response format:', returnNotesResult);
        throw new Error(returnNotesResult?.message || 'Failed to fetch return notes');
      } setSuppliers(suppliersResponse?.data || suppliersResponse || []);
    } catch (err) {
      console.error('❌ Error fetching data:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to fetch data');
      setReturnNotes([]);
      setSuppliers([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('supplierReturnNoteManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    // Page reset will be handled by the useEffect that watches debouncedSearchTerm
  };

  const handleStatusFilter = (e) => {
    const newStatus = e.target.value;
    if (newStatus !== statusFilter) {
      setStatusFilter(newStatus);
      // Page reset will be handled by the useEffect that watches statusFilter
    }
  };

  const handleSupplierFilter = (e) => {
    const newSupplier = e.target.value;
    if (newSupplier !== supplierFilter) {
      setSupplierFilter(newSupplier);
      // Page reset will be handled by the useEffect that watches supplierFilter
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortOrder('desc');
    }
    // Page reset will be handled by the useEffect that watches sortField/sortOrder
  };

  const handleCreateReturnNote = () => {
    setEditingReturnNote(null);
    setShowForm(true);
  };

  const handleEditReturnNote = async (returnNote) => {
    try {
      // Fetch detailed return note data
      const response = await inventoryApi.getSupplierReturnNoteById(returnNote._id);
      const detailedReturnNote = response.data;

      setEditingReturnNote(detailedReturnNote);
      setShowForm(true);
    } catch (error) {
      console.error('❌ Failed to fetch return note details:', error);
      toast.error(error.response?.data?.message || 'Failed to load return note details');
    }
  };

  const handleViewReturnNote = async (returnNote) => {
    try {
      // Fetch fully-populated return note (list rows omit supplier contact info, etc.)
      const response = await inventoryApi.getSupplierReturnNoteById(returnNote._id);
      setSelectedReturnNote(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('❌ Failed to fetch return note details:', error);
      toast.error(error.response?.data?.message || 'Failed to load return note details');
    }
  };
  // opened from a link elsewhere (e.g. Vendor History) with ?view=<return note id>
  useOpenFromQuery((id) => handleViewReturnNote({ _id: id }));

  const handleDeleteReturnNote = async (returnNote) => {
    try {
      await inventoryApi.deleteSupplierReturnNote(returnNote._id);
      setShowDeleteConfirm(null);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete return note:', error);
      toast.error(error.response?.data?.message || 'Failed to delete return note');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingReturnNote(null);
    fetchData();
  };

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, supplierFilter, sortField, sortOrder]);



  const handleApproveReturnNote = (returnNote) => {
    setShowApproveConfirm(returnNote);
  };

  const confirmApproveReturnNote = async (returnNote) => {
    try {
      setActionLoading(`approve-${returnNote._id}`);
      const response = await inventoryApi.approveSupplierReturnNote(returnNote._id, {
        approved: true,
        approvedBy: 'current_user', // You may want to get this from auth context
        approvedAt: new Date(),
        comments: 'Approved via management interface'
      });
      setShowApproveConfirm(null);
      await fetchData();
    } catch (error) {
      console.error('❌ Failed to approve return note:', error);
      console.error('❌ Error details:', error.response?.data);
      toast.error(error.response?.data?.message || error.message || 'Failed to approve return note');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectReturnNote = async (returnNote) => {
    const reason = prompt(`Please provide a reason for rejecting return note "${returnNote.returnNoteNumber}":`);
    if (reason !== null && reason.trim()) {
      try {
        setActionLoading(`reject-${returnNote._id}`);
        const response = await inventoryApi.rejectSupplierReturnNote(returnNote._id, {
          reason: reason.trim(),
          comments: reason.trim(),
          rejectedBy: 'current_user', // You may want to get this from auth context
          rejectedAt: new Date()
        });
        await fetchData();
      } catch (error) {
        console.error('❌ Failed to reject return note:', error);
        console.error('❌ Error details:', error.response?.data);
        toast.error(error.response?.data?.message || error.message || 'Failed to reject return note');
      } finally {
        setActionLoading(null);
      }
    }
  };

  const clearError = () => {
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      setCurrentPage(page);
    } else {
      console.warn('❌ Invalid page change attempt:', { page, currentPage, totalPages, loading });
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page when changing items per page
    }
  };

  const renderSortableHeader = (field, label) => {
    const isActive = sortField === field;
    const isAsc = sortOrder === 'asc';

    return (
      <button
        onClick={() => handleSort(field)}
        className="group inline-flex items-center space-x-1 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600 transition-colors"
      >
        <span>{label}</span>
        <div className="flex flex-col">
          <svg
            className={`w-3 h-3 ${isActive && !isAsc ? 'text-indigo-600' : 'text-gray-400'} group-hover:text-indigo-500 transition-colors`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          <svg
            className={`w-3 h-3 -mt-1 ${isActive && isAsc ? 'text-indigo-600' : 'text-gray-400'} group-hover:text-indigo-500 transition-colors`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
    );
  };

  const getReturnNoteIcon = (returnNote) => {
    return returnNote.returnNoteNumber.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processed':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'pending_approval': 'Pending Approval',
      'approved': 'Approved',
      'processed': 'Processed',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled',
      'completed': 'Completed'
    };
    return statusMap[status] || status;
  };

  const canEdit = (returnNote) => {
    return hasPermission(PERMISSIONS.UPDATE) && ['pending_approval', 'rejected'].includes(returnNote.status);
  };

  const canDelete = (returnNote) => {
    return hasPermission(PERMISSIONS.DELETE) && ['pending_approval', 'rejected'].includes(returnNote.status);
  };

  const canApprove = (returnNote) => {
    return hasPermission(PERMISSIONS.APPROVE) && returnNote.status === 'pending_approval';
  };

  const canReject = (returnNote) => {
    return hasPermission(PERMISSIONS.APPROVE) && returnNote.status === 'pending_approval';
  };

  const canCreate = () => {
    return hasPermission(PERMISSIONS.CREATE);
  };

  const canView = () => {
    return hasPermission(PERMISSIONS.READ);
  };

  const renderReturnNoteCard = (returnNote) => {
    return (
      <div key={returnNote._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-42 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                {getReturnNoteIcon(returnNote)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {returnNote.returnNoteNumber}
                </h3>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              {canView() && (
                <button
                  onClick={() => handleViewReturnNote(returnNote)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                  title="View return note details"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              )}

              {canApprove(returnNote) && (
                <button
                  onClick={() => handleApproveReturnNote(returnNote)}
                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                  title="Approve return note"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              )}

              {canEdit(returnNote) && (
                <button
                  onClick={() => handleEditReturnNote(returnNote)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                  title="Edit return note"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}

              {canDelete(returnNote) && (
                <button
                  onClick={() => setShowDeleteConfirm(returnNote)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete return note"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 mb-2">
            <p className="text-xs text-gray-500 truncate">
              GRN: {returnNote.grnNumber}
            </p>
            <p className="text-xs text-gray-600 truncate">
              Supplier: {returnNote.supplierName}
            </p>
            <p className="text-xs text-gray-500">
              Date: {new Date(returnNote.returnDate).toLocaleDateString()}
            </p>
          </div>

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusColor(returnNote.status)}`}>
              {getStatusDisplay(returnNote.status)}
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
              {returnNote.itemCount} items
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              {(returnNote.totals?.totalWithTax || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderReturnNoteRow = (returnNote) => {
    return (
      <tr key={returnNote._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-sm font-semibold text-indigo-700">
              {getReturnNoteIcon(returnNote)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{returnNote.returnNoteNumber}</div>
              <div className="text-xs text-gray-500">GRN: {returnNote.grnNumber}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">{returnNote.supplierName}</div>
          <div className="text-xs text-gray-500">{returnNote.supplierCode}</div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {new Date(returnNote.returnDate).toLocaleDateString()}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">{returnNote.itemCount} items</div>
          <div className="text-xs text-gray-500">
            {(returnNote.totals?.totalWithTax || 0).toFixed(2)}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(returnNote.status)}`}>
            {getStatusDisplay(returnNote.status)}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            {canView() && (
              <button
                onClick={() => handleViewReturnNote(returnNote)}
                className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-all"
                title="View return note"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}

            {canApprove(returnNote) && (
              <button
                onClick={() => handleApproveReturnNote(returnNote)}
                className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-all"
                title="Approve return note"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )}

            {canReject(returnNote) && (
              <button
                onClick={() => handleRejectReturnNote(returnNote)}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Reject return note"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {canEdit(returnNote) && (
              <button
                onClick={() => handleEditReturnNote(returnNote)}
                className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                title="Edit return note"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}

            {canDelete(returnNote) && (
              <button
                onClick={() => setShowDeleteConfirm(returnNote)}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Delete return note"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  if (loading && returnNotes.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={clearError}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <SupplierReturnNoteForm
            returnNote={editingReturnNote}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowForm(false);
              setEditingReturnNote(null);
            }}
          />
        )}

        {/* Main Content */}
        {!showForm && (
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                {/* Search */}
                <div className="flex-1">
                  <TextInput
                    variant="input"
                    type="text"
                    placeholder="Search return notes by number, GRN, supplier..."
                    value={searchTerm}
                    onChange={handleSearch}
                    icon={({ className }) => (
                      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                    className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                    disabled={loading}
                  />
                  {/* Search loading indicator */}
                  {loading && debouncedSearchTerm !== searchTerm && (
                    <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                      <svg className="animate-spin h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Filters and Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {/* Status Filter */}
                  <CustomSelect
                    value={statusFilter}
                    onChange={handleStatusFilter}
                    options={returnNoteStatuses}
                    size="sm"
                    variant="default"
                    className="min-w-[140px]"
                  />

                  {/* Supplier Filter */}
                  <CustomSelect
                    value={supplierFilter}
                    onChange={handleSupplierFilter}
                    options={[
                      { value: 'all', label: 'All Suppliers' },
                      ...suppliers.map(supplier => ({
                        value: supplier._id,
                        label: supplier.name
                      }))
                    ]}
                    size="sm"
                    variant="default"
                    className="min-w-[140px]"
                  />

                  {/* Sort Options */}
                  <div className="flex items-center gap-1">
                    <CustomSelect
                      value={`${sortField}-${sortOrder}`}
                      onChange={(e) => {
                        const [field, order] = e.target.value.split('-');
                        setSortField(field);
                        setSortOrder(order);
                        setCurrentPage(1);
                      }}
                      options={[
                        { value: 'returnDate-desc', label: 'Latest First' },
                        { value: 'returnDate-asc', label: 'Oldest First' },
                        { value: 'returnNoteNumber-asc', label: 'Number A-Z' },
                        { value: 'returnNoteNumber-desc', label: 'Number Z-A' },
                        { value: 'supplierName-asc', label: 'Supplier A-Z' },
                        { value: 'supplierName-desc', label: 'Supplier Z-A' },
                        { value: 'status-asc', label: 'Status A-Z' }
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[120px]"
                    />
                  </div>

                  {/* View Toggle */}
                  <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                    <button
                      onClick={() => handleViewChange('card')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="Card view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Cards
                    </button>
                    <button
                      onClick={() => handleViewChange('list')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="List view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      List
                    </button>
                  </div>

                  {/* Refresh Button */}
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md border border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh data"
                  >
                    <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  {/* Add Return Note Button */}
                  {canCreate() && (
                    <button
                      onClick={handleCreateReturnNote}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Return Note
                    </button>
                  )}

                </div>
              </div>
            </div>
            {/* Pagination */}
            {totalItems > 0 && totalPages > 0 && (
              <div className="flex items-center justify-between pb-2 border-b  px-2  py-2 bg-gradient-to-r from-gray-100 to-gray-100 mb-6 border-b border-gray-200">
                {/* Results Info */}
                <div className="flex items-center gap-2">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} return notes
                    </p>
                    <p className="text-xs text-gray-500">
                      {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' ? 'Filtered results' : 'All return notes'}
                      {totalItems > 0 && ` • Sorted by ${sortField === 'returnDate' ? 'date' :
                        sortField === 'returnNoteNumber' ? 'number' :
                          sortField === 'supplierName' ? 'supplier' :
                            sortField} (${sortOrder === 'desc' ? 'newest first' : 'oldest first'})`}
                      {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
                    </p>
                  </div>
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center gap-3">
                  {/* Items per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 font-medium">Show:</span>
                    <CustomSelect
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      disabled={loading}
                      options={[
                        { value: 10, label: '10' },
                        { value: 20, label: '20' },
                        { value: 50, label: '50' },
                        { value: 100, label: '100' }
                      ]}
                      size="sm"
                      variant="minimal"
                      className="min-w-[70px]"
                    />
                  </div>

                  {/* Navigation Buttons - Only show if more than one page */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage <= 1 || loading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="First page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || loading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="hidden sm:inline">Previous</span>
                      </button>                          {/* Page Numbers */}
                      <div className="hidden md:flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 7;

                          if (totalPages <= maxVisiblePages) {
                            // Show all pages if 7 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Smart pagination for more than 7 pages
                            if (currentPage <= 4) {
                              // Near the beginning: show 1,2,3,4,5...last
                              for (let i = 1; i <= 5; i++) {
                                pages.push(i);
                              }
                              pages.push('...');
                              pages.push(totalPages);
                            } else if (currentPage >= totalPages - 3) {
                              // Near the end: show 1...last-4,last-3,last-2,last-1,last
                              pages.push(1);
                              pages.push('...');
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // In the middle: show 1...current-1,current,current+1...last
                              pages.push(1);
                              pages.push('...');
                              for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                pages.push(i);
                              }
                              pages.push('...');
                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, index) => {
                            if (page === '...') {
                              return (
                                <span key={`ellipsis-${index}`} className="px-2 text-gray-400 text-sm">
                                  ...
                                </span>
                              );
                            }

                            const isCurrentPage = page === currentPage;

                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                disabled={loading}
                                className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                                  ? 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-50'
                                  }`}
                              >
                                {page}
                              </button>
                            );
                          });
                        })()}
                      </div>

                      {/* Mobile page indicator */}
                      <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                        <span className="text-sm font-medium text-gray-900">{currentPage}</span>
                        <span className="text-sm text-gray-500 mx-1">of</span>
                        <span className="text-sm font-medium text-gray-900">{totalPages}</span>
                      </div>

                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage >= totalPages || loading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage >= totalPages || loading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="Last page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-2 relative">
              {/* Loading overlay */}
              {loading && returnNotes.length > 0 && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-gray-600">Loading...</span>
                  </div>
                </div>
              )}

              {returnNotes.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No return notes found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by creating your first supplier return note.'
                    }
                  </p>
                  {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' ? (
                    <div className="mt-6 space-y-3">
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setDebouncedSearchTerm('');
                          setStatusFilter('all');
                          setSupplierFilter('all');
                          setSortField('returnDate');
                          setSortOrder('desc');
                          setCurrentPage(1);
                        }}
                        className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md border border-gray-300 transition-all"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear all filters
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6">
                      {canCreate() ? (
                        <button
                          onClick={handleCreateReturnNote}
                          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Create Return Note
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500">
                          You don't have permission to create return notes.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Card View */}
                  {view === 'card' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {returnNotes.map(renderReturnNoteCard)}
                    </div>
                  )}

                  {/* List View */}
                  {view === 'list' && (
                    <div className="overflow-x-auto">
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left">
                              {renderSortableHeader('returnNoteNumber', 'Return Note')}
                            </th>
                            <th className="px-4 py-3 text-left">
                              {renderSortableHeader('supplierName', 'Supplier')}
                            </th>
                            <th className="px-4 py-3 text-left">
                              {renderSortableHeader('returnDate', 'Date')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Details
                            </th>
                            <th className="px-4 py-3 text-center">
                              {renderSortableHeader('status', 'Status')}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {returnNotes.map(renderReturnNoteRow)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Return Note</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete return note <span className="font-medium text-gray-900">"{showDeleteConfirm.returnNoteNumber}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteReturnNote(showDeleteConfirm)}
                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                  >
                    Delete Return Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve Confirmation Modal */}
        {showApproveConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Approve Return Note</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to approve return note <span className="font-medium text-gray-900">"{showApproveConfirm.returnNoteNumber}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This will add the returned items back to stock and allow payment setoff against supplier accounts. This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowApproveConfirm(null)}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => confirmApproveReturnNote(showApproveConfirm)}
                    disabled={actionLoading === `approve-${showApproveConfirm._id}`}
                    className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `approve-${showApproveConfirm._id}` ? 'Approving...' : 'Approve Return Note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Return Note Details */}
        {selectedReturnNote && showDetails && (
          <SupplierReturnNoteDetails
            open={showDetails}
            returnNote={selectedReturnNote}
            onClose={() => {
              setShowDetails(false);
              setSelectedReturnNote(null);
            }}
            onReturnNoteUpdate={fetchData}
          />
        )}
    </div>
  );
};

export default SupplierReturnNoteManagement;